import React, { useEffect, useState } from 'react';
import { FormSelect, Row, Col, FormInput, FormRadioGroup, Heading, InPageAlert } from '@snsw/react-component-library';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import { radioOptionsYesNo } from 'shared/constants/constants';
import { InspectionDocumentsUpload } from '../InspectionDocumentsUpload';
import styles from '../PlantItemDetails.module.scss';
import { RefData } from 'plantItemNew/constants/refData';
import { PlantItemDetailsPage } from 'plantItemNew/constants/plantItemDetailsPage';

export const TowerCraneDetails = React.forwardRef((props, ref) => {
  const { value = {}, onChange = () => {}, applicationId, refData = {}, onErrorChange = () => {} } = props;

  const {
    typeOfTowerCrane: savedTypeOfTowerCrane = '',
    maxRadius: savedMaxRadius = '',
    maxHeight: savedMaxHeight = '',
    maxRatedCapacity: savedMaxRatedCapacity = '',
    worksiteRegistered: savedWorksiteRegistered = '',
    inspectionDetails: savedInspectionDetails = {},
  } = value;

  const [typeOfTowerCrane, setTypeOfTowerCrane] = useState(savedTypeOfTowerCrane);
  const [maxRadius, setMaxRadius] = useState(savedMaxRadius);
  const [maxRatedCapacity, setMaxRatedCapacity] = useState(savedMaxRatedCapacity);
  const [maxHeight, setMaxHeight] = useState(savedMaxHeight);
  const [worksiteRegistered, setWorksiteRegistered] = useState(savedWorksiteRegistered);
  const [inspectionDetails, setInspectionDetails] = useState(savedInspectionDetails);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({
    typeOfTowerCrane: false,
    maxRadius: false,
    maxRatedCapacity: false,
    maxHeight: false,
  });
  const [errorSummary, setErrorSummary] = useState([]);
  const [inspectionErrorSummary, setInspectionErrorSummary] = useState([]);

  const validators = [
    {
      fieldName: 'typeOfTowerCrane',
      fieldValue: typeOfTowerCrane,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'maxRadius',
      fieldValue: maxRadius,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxHeight',
      fieldValue: maxHeight,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxRatedCapacity',
      fieldValue: maxRatedCapacity,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'worksiteRegistered',
      fieldValue: worksiteRegistered,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
  ];

  const towerCraneTypeOptions = Array.isArray(refData[RefData.towerCrane.subTypeOptions])
    ? refData[RefData.towerCrane.subTypeOptions].map((e) => ({
        value: e.value,
        text: e.description,
      }))
    : [];

  if (ref) {
    ref.current = {
      validate: () => {
        return validateAll();
      },
    };
  }

  const inspectionComponentRef = React.useRef();
  const supportingDocumentCode = refData.documentType.find((d) => d.description === 'Supporting Documents');

  const validateAll = () => {
    let isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);

    setShowErrors(!isCurrentComponentValid);

    let inspectionDetailValid = true;

    if (worksiteRegistered === 'yes' && inspectionComponentRef.current) {
      inspectionDetailValid = inspectionComponentRef.current.validate();
    }

    return isCurrentComponentValid && inspectionDetailValid;
  };

  useEffect(() => {
    let details = {
      typeOfTowerCrane,
      maxRadius,
      maxRatedCapacity,
      maxHeight,
      worksiteRegistered,
      ...(worksiteRegistered === 'yes' && { inspectionDetails }),
      ...(worksiteRegistered === 'no' && { inspectionDetails: undefined }),
      attachments: inspectionDetails?.attachments || [],
    };
    onChange(details);

    if (showErrors) validateAll();
  }, [typeOfTowerCrane, maxRadius, maxRatedCapacity, maxHeight, worksiteRegistered, inspectionDetails]);

  useEffect(() => {
    onErrorChange(errorSummary.concat(inspectionErrorSummary));
  }, [errorSummary, inspectionErrorSummary]);

  return (
    <>
      <Row>
        <Col span={6}>
          <FormSelect
            id="typeOfTowerCrane"
            label={PlantItemDetailsPage.relatedDetails.towerCrane.typeOfTowerCraneLabel}
            placeholder="Please select"
            options={towerCraneTypeOptions}
            onChange={(e) => {
              setTypeOfTowerCrane(e.target.value);
            }}
            value={typeOfTowerCrane}
            errorMessage={errors?.typeOfTowerCrane?.errorMessage}
            hasError={showErrors && errors?.typeOfTowerCrane?.invalid}
            inputWidth="md"
          />
        </Col>
      </Row>

      <Row style={{ marginTop: 20 }}>
        <Col span={4}>
          <FormInput
            id="maxRadius"
            label={PlantItemDetailsPage.relatedDetails.towerCrane.maxRadiusLabel}
            suffix="m"
            onChange={(e, { value }) => setMaxRadius(value)}
            value={maxRadius}
            errorMessage={errors?.maxRadius?.errorMessage}
            hasError={showErrors && errors?.maxRadius?.invalid}
            className={styles.inputWithSuffix}
          />

          <FormInput
            id="maxHeight"
            label={PlantItemDetailsPage.relatedDetails.towerCrane.maxHeightLabel}
            suffix="m"
            onChange={(e, { value }) => setMaxHeight(value)}
            value={maxHeight}
            errorMessage={errors?.maxHeight?.errorMessage}
            hasError={showErrors && errors?.maxHeight?.invalid}
            className={styles.inputWithSuffix}
          />

          <FormInput
            id="maxRatedCapacity"
            label={PlantItemDetailsPage.relatedDetails.towerCrane.maxRatedCapacityLabel}
            suffix="t"
            onChange={(e, { value }) => setMaxRatedCapacity(value)}
            value={maxRatedCapacity}
            errorMessage={errors?.maxRatedCapacity?.errorMessage}
            hasError={showErrors && errors?.maxRatedCapacity?.invalid}
            className={styles.inputWithSuffix}
          />
        </Col>
      </Row>
      <Row>
        <Col span={9}>
          <FormRadioGroup
            id="worksiteRegistered"
            name="worksiteRegistered"
            vertical={true}
            options={radioOptionsYesNo}
            onChange={setWorksiteRegistered}
            value={worksiteRegistered}
            errorMessage={errors?.worksiteRegistered?.errorMessage}
            hasError={showErrors && errors?.worksiteRegistered?.invalid}
            legend={PlantItemDetailsPage.relatedDetails.towerCrane.worksiteRegisteredLabel}
            margin={{ top: 'lg' }}
          />

          {worksiteRegistered === 'yes' && (
            <>
              <Heading level={3}>Tower crane foundation design statement</Heading>
              <InPageAlert
                className={styles.pageAlert}
                variant="info"
                title="When a tower crane is being item registered to a worksite, a designer’s (engineer) statement regarding the crane’s foundations is required."
              >
                <p>
                  The foundation design statement can be downloaded{' '}
                  <a
                    href="https://www.safework.nsw.gov.au/__data/assets/pdf_file/0007/1069828/tower-crane-foundation-design-statement.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>
                  . You must upload this completed document to submit your application.
                </p>
                <p>Some of the details from the foundation design statement is required below.</p>
              </InPageAlert>
              <InspectionDocumentsUpload
                ref={inspectionComponentRef}
                value={inspectionDetails}
                onChange={setInspectionDetails}
                applicationId={applicationId}
                onErrorChange={setInspectionErrorSummary}
                supportingDocumentCode={supportingDocumentCode}
                description="TC Foundation statement"
                labels={PlantItemDetailsPage.relatedDetails.towerCraneFoundationInspection}
              />
            </>
          )}
          {worksiteRegistered === 'no' && (
            <InPageAlert
              variant="info"
              title="If this item is moved to a worksite, you must complete a Notification of Relocation of Registered Plant form and provide a foundation inspection statement at that time."
            ></InPageAlert>
          )}
        </Col>
      </Row>
    </>
  );
});

TowerCraneDetails.displayName = 'TowerCraneDetails';
